import { DesignData, ElementAddress, MediaText } from 'editor/src/store/design/types';
import { FontDefinition, UploadedFontData } from 'editor/src/store/fonts/types';

export interface MissingFontRelink {
  elementAddress: ElementAddress;
  elementUpdate: Partial<MediaText>;
}

/**
 * Texts whose font is a file name nobody uploaded (an InDesign import keeps the packaged file's
 * name when its upload fails), pointed at an uploaded font of that file name. The name is all the
 * design kept, so a customer uploading the file is what repairs the text.
 */
function getMissingFontRelinks(
  designData: DesignData | undefined,
  availableFonts: FontDefinition[],
  uploadedFonts: UploadedFontData[],
): MissingFontRelink[] {
  const customerFonts = uploadedFonts.filter((font) => !font.isMissing);
  if (!designData || !customerFonts.length) {
    return [];
  }

  // Uploaded fonts arrive newest first; the newest upload of a file name wins.
  const fontFileByFilename = new Map<string, string>();
  customerFonts.forEach((font) => {
    const filename = font.filename.toLowerCase();
    if (!fontFileByFilename.has(filename)) {
      fontFileByFilename.set(filename, font.fontFile);
    }
  });
  const isKnownFont = (fontFamily: string) =>
    customerFonts.some((font) => font.fontFile === fontFamily) ||
    availableFonts.some((font) => font.metadata.fontFile === fontFamily);
  // The file-name lookup goes first: it runs on every design change and almost never matches.
  const resolve = (fontFamily: string | undefined) => {
    if (!fontFamily) {
      return undefined;
    }
    const fontFile = fontFileByFilename.get(fontFamily.toLowerCase());
    return fontFile && !isKnownFont(fontFamily) ? fontFile : undefined;
  };

  const relinks: MissingFontRelink[] = [];
  designData.spreads.forEach((spread, spreadIndex) => {
    spread.pages.forEach((page, pageIndex) => {
      page.groups.media?.forEach((element, elementIndex) => {
        if (element.type !== 'text') {
          return;
        }

        const fontFamily = resolve(element.extra.fontFamily);
        const variants = element.extra.textVariants;
        let variantsChanged = false;
        const values = variants
          ? Object.fromEntries(
              Object.entries(variants.values).map(([optionId, variant]) => {
                const variantFontFamily = resolve(variant.fontFamily);
                variantsChanged ||= !!variantFontFamily;
                return [optionId, variantFontFamily ? { ...variant, fontFamily: variantFontFamily } : variant];
              }),
            )
          : undefined;

        if (!fontFamily && !variantsChanged) {
          return;
        }

        const extra = { ...element.extra, fontFamily: fontFamily ?? element.extra.fontFamily };
        if (variants && values && variantsChanged) {
          extra.textVariants = { ...variants, values };
        }
        const elementUpdate: Partial<MediaText> = { extra };
        // The outline was drawn with whatever font was packaged; the uploaded one renders afresh.
        if (fontFamily) {
          elementUpdate.path = undefined;
        }
        relinks.push({ elementAddress: { spreadIndex, pageIndex, elementIndex }, elementUpdate });
      });
    });
  });
  return relinks;
}

export default getMissingFontRelinks;
